import type { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  /** Extra classes, appended so callers can add padding, spans or state. */
  className?: string;
  /** Inner padding preset. Pass `none` when the card owns its own sections
   *  (a data table with a toolbar, a list with dividers). */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /**
   * Lifts the card on hover. Only for cards that are themselves a link or
   * button — a hover state on a static panel promises an interaction that
   * isn't there.
   */
  interactive?: boolean;
  as?: 'div' | 'section' | 'article';
}

/**
 * The admin console's surface — design system v2 `.panel`: **white, 1px
 * ink-200 hairline, radius 16, no resting shadow.** Elevation is reserved for
 * things that float (menus, drawers, modals) or that respond to the pointer.
 *
 * The counterpart to `UtsaahaCard` in the Flutter kit, so a panel in the
 * console and a card in the apps read as the same material.
 */
export function Card({
  children,
  className = '',
  padding = 'md',
  interactive = false,
  as: Tag = 'div',
}: CardProps) {
  const pad =
    padding === 'none' ? '' : padding === 'sm' ? 'p-4' : padding === 'lg' ? 'p-6' : 'p-5';
  return (
    <Tag
      className={[
        'panel overflow-hidden',
        pad,
        interactive
          ? 'transition-[box-shadow,border-color] duration-micro ease-brand hover:border-surface-line-strong hover:shadow-elev-2'
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </Tag>
  );
}

/** Panel heading row: 16/600 title, optional muted line, right-side slot. */
export function CardHeader({
  title,
  description,
  actions,
  className = '',
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-[16px] font-semibold leading-snug text-ink-900">{title}</h3>
        {description && <p className="mt-0.5 text-[13.5px] text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
